import SidebarItem from '@/src/components/sidebar/sidebarItem'
import SubscriptionIcon from '@/src/components/sidebar/icons/subscription'
import { canAccessPlansSection } from '@/src/utils/permissions'
import { TABS } from '@dolfin/business/custom_types'
import { useHasRecentSubscriptionData } from '@dolfin/hooks/api/v1/users/useHasRecentSubscriptionData'
import { useIssueUnreadActivity } from '@dolfin/hooks/useIssueUnreadActivity'
import { useOrgContext } from '@dolfin/hooks/state/organization'
import { isFlipperEnabledForViewer } from '@dolfin/business/constants/flipper'
import FlipperChatButton from '@dolfin/ui/components/flipper/FlipperChatButton'
import Notifications from '@dolfin/ui/components/notifications'
import { zIndexes } from '@dolfin/ui/constants/z-index'
import DolfinIcon from '@dolfin/ui/svgs/dolfin-icon'
import DolfinLogo from '@dolfin/ui/svgs/dolfin-logo'
import Avatar from '@dolfin/ui/ui/avatar'
import { Sizes } from '@dolfin/ui/ui/avatar/constants'
import { UserGroupIcon } from '@heroicons/react/24/outline'
import { Bars3Icon, XMarkIcon } from '@heroicons/react/24/solid'
import { t } from '@lingui/core/macro'
import { Trans } from '@lingui/react/macro'
import {
  AlertCircle,
  AnnotationCheck,
  Award04,
  BarChartSquare02,
  Dataflow01,
  FaceSmile,
  File05,
  HeartHand,
  LayoutAlt03,
  Link03,
  Rows02,
  Ruler,
  Settings02,
  Sliders02,
  Target01,
  Target04,
  Trophy01,
  User01,
  Users01
} from '@untitled-ui/icons-react'
import Link from 'next/link'
import { useRouter } from 'next/router'
import { useCallback, useRef, useState } from 'react'
import { Button } from 'react-aria-components'
import { twMerge } from 'tailwind-merge'
import { SidebarLink } from './constants'

interface SidebarLinkProps {
  full?: boolean
  closeSidebar: () => void
}

const SidebarElement = ({ full = false, closeSidebar }: SidebarLinkProps) => {
  const router = useRouter()

  const {
    isAdmin,
    isSupervisor,
    hasFullView,
    isTeamManager,
    selectedOrganization,
    role,
    currentUser
  } = useOrgContext(state => ({
    isAdmin: state.getRole() === 'admin',
    isSupervisor: state.getRole() === 'supervisor',
    role: state.getRole(),
    isTeamManager: state.isManager(),
    hasFullView: state.hasFullView(),
    currentUser: state.profile,
    selectedOrganization: state.selectedOrganization
  }))
  const canAccessPlans = canAccessPlansSection(role)
  const { unreadIssueCount } = useIssueUnreadActivity()
  const shouldShowSubscriptionLink = !isAdmin && !isSupervisor && !hasFullView
  const { data: hasRecentSubscriptionData } = useHasRecentSubscriptionData({
    organizationId: selectedOrganization?.id,
    profileId: currentUser?.id,
    enabled: shouldShowSubscriptionLink
  })

  const replaceExceptionalRouting = (route: string) => {
    switch (route?.toUpperCase()) {
      case TABS.USER:
        return TABS.USERS.toLowerCase()
      case TABS.TEAM:
        return TABS.TEAMS.toLowerCase()
      default:
        return route
    }
  }

  const TOP_SECTION: SidebarLink[] = [
    ...(hasFullView && !isAdmin && !isSupervisor
      ? [
          {
            id: TABS.DASHBOARD,
            href: '/dashboard',
            label: t`Dashboard`,
            Icon: BarChartSquare02
          },
          {
            id: TABS.MY_TEAM,
            label: t`My team`,
            href: '/dashboard?page=my_team',
            Icon: UserGroupIcon
          }
        ]
      : [
          {
            id: TABS.DASHBOARD,
            href: '/dashboard',
            label: t`Dashboard`,
            Icon: BarChartSquare02
          }
        ]),
    ...(shouldShowSubscriptionLink && hasRecentSubscriptionData === true
      ? [
          {
            id: TABS.SUBSCRIPTIONS,
            label: t`Subscriptions`,
            href: '/dashboard?page=subscriptions',
            Icon: SubscriptionIcon
          }
        ]
      : []),
    {
      id: TABS.COMPENSATIONS,
      label: t`Compensations`,
      href: '/dashboard?page=compensations',
      Icon: HeartHand
    },
    {
      id: TABS.ISSUES,
      label: t`Disputes`,
      href: '/dashboard?page=issues',
      Icon: AlertCircle,
      unreadCount: unreadIssueCount
    },
    ...(canAccessPlans && !isAdmin && !isSupervisor
      ? [
          {
            id: TABS.PLANS,
            label: t`Plans`,
            Icon: Dataflow01,
            href: '/dashboard?page=plans'
          }
        ]
      : []),
    ...(isAdmin || isSupervisor || isTeamManager
      ? [
          {
            id: TABS.STATEMENTS,
            label: t`Statements`,
            href: '/dashboard?page=statements',
            Icon: File05
          }
        ]
      : [])
  ]

  const MIDDLE_SECTION: SidebarLink[] = [
    ...(isAdmin || isSupervisor || hasFullView
      ? [
          {
            id: TABS.USERS,
            label: t`Users & Teams`,
            href: '/dashboard?page=users',
            Icon: FaceSmile,
            subLinks: [
              {
                id: TABS.USERS,
                label: t`Users`,
                href: '/dashboard?page=users',
                Icon: User01
              },
              {
                id: 'business-units',
                label: t`Business Units`,
                href: '/dashboard?page=business-units',
                Icon: Rows02
              },
              {
                id: TABS.TEAMS,
                label: t`Teams`,
                href: '/dashboard?page=teams',
                Icon: Users01
              }
            ]
          }
        ]
      : []),
    ...(isAdmin
      ? [
          {
            id: TABS.COMPENSATION_FRAMEWORK,
            label: t`Compensation framework`,
            href: '/dashboard?page=compensation_framework',
            Icon: Sliders02,
            subLinks: [
              {
                id: TABS.PLANS,
                label: t`Plans`,
                Icon: Dataflow01,
                href: '/dashboard?page=plans'
              },
              {
                id: TABS.TARGETS,
                label: t`Targets`,
                Icon: Target01,
                href: '/dashboard?page=targets'
              },
              {
                id: TABS.METRICS,
                label: t`Metrics`,
                href: '/dashboard?page=metrics',
                Icon: Ruler
              },
              {
                id: TABS.APPROVAL_FLOW,
                label: t`Approval flow`,
                Icon: AnnotationCheck,
                href: '/dashboard?page=approval_flow'
              },
              {
                id: TABS.TARGET_ACHIEVEMENTS,
                label: t`Target achievements`,
                Icon: Target04,
                href: '/dashboard?page=target_achievements'
              }
            ]
          }
        ]
      : hasFullView || isSupervisor
        ? [
            {
              id: TABS.TARGET_ACHIEVEMENTS,
              label: t`Target achievements`,
              Icon: Target04,
              href: '/dashboard?page=target_achievements'
            }
          ]
        : []),
    ...(isAdmin || isTeamManager
      ? [
          {
            id: TABS.MOTIVATIONAL_FEATURES,
            label: t`Motivational features`,
            href: '/dashboard?page=challenges',
            Icon: Trophy01,
            subLinks: [
              {
                id: TABS.CHALLENGES,
                label: t`Challenges`,
                href: '/dashboard?page=challenges',
                Icon: Trophy01
              },
              {
                id: TABS.BADGE_CREATOR,
                label: t`Badges`,
                href: '/dashboard?page=badge_creator',
                Icon: Award04
              }
            ]
          }
        ]
      : []),
    {
      id: TABS.DATA,
      href: '/dashboard?page=data',
      label: t`Data`,
      Icon: LayoutAlt03
    },
    ...(isAdmin
      ? [
          {
            id: TABS.INTEGRATIONS,
            label: t`Integrations`,
            href: '/dashboard?page=integrations',
            Icon: Link03
          }
        ]
      : [])
  ]

  const isActiveById = useCallback(
    (id: string | number): boolean => {
      const page = router.query.page

      if (page === 'kpis' || page === 'custom_records') {
        if (typeof id !== 'string') {
          return false
        }

        const [kind, tableId] = id.split(':')

        return kind === page && tableId === String(router.query.table)
      }

      return page
        ? new RegExp(`(${id})`, 'gi').test(
            replaceExceptionalRouting(page as string)
          )
        : id === TABS.DASHBOARD
    },
    [router.query.page, router.query.table]
  )

  const defaultSubSidebar = useCallback(() => {
    return (
      TOP_SECTION.find(link =>
        link.subLinks?.some(subLink => isActiveById(subLink.id))
      )?.id || null
    )
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [JSON.stringify(TOP_SECTION), isActiveById])

  const isActive = (
    item: Omit<SidebarLink, 'id'> & { id: TABS | number | string }
  ) => {
    return isActiveById(item.id)
  }

  const isChildrenActive = (item: SidebarLink) => {
    if (item.subLinks && item.subLinks.length > 0) {
      return item.subLinks.some(subItem => isActiveById(subItem.id))
    }
    return false
  }

  const [visibleSubSidebar, setVisibleSubSidebar] = useState<string | null>(
    defaultSubSidebar()
  )
  const [isHovering, setIsHovering] = useState<boolean>(false)
  const [isSidebarFullyExpanded, setIsSidebarFullyExpanded] =
    useState<boolean>(false)
  const expansionTimeout = useRef<ReturnType<typeof setTimeout> | null>(null)

  const HOVER_DELAY = 100
  const SIDEBAR_TRANSITION_DURATION = 300
  const delayHover = (fn: () => void) => {
    setTimeout(() => {
      fn()
    }, HOVER_DELAY)
  }

  const handleMouseEnter = () => {
    if (expansionTimeout.current) {
      clearTimeout(expansionTimeout.current)
    }

    expansionTimeout.current = setTimeout(() => {
      setIsSidebarFullyExpanded(true)
    }, HOVER_DELAY + SIDEBAR_TRANSITION_DURATION)
    delayHover(() => setIsHovering(true))
  }

  const handleMouseLeave = () => {
    if (expansionTimeout.current) {
      clearTimeout(expansionTimeout.current)
      expansionTimeout.current = null
    }

    setIsSidebarFullyExpanded(false)
    delayHover(() => setIsHovering(false))
  }

  return (
    <div
      onMouseEnter={handleMouseEnter}
      onMouseLeave={handleMouseLeave}
      className={twMerge(
        `${
          full
            ? 'w-full h-[calc(100vh-2rem)]'
            : 'w-[3.45rem] transition-all duration-300 ease-in-out hover:w-60 bottom-0 h-screen'
        } group fixed left-0 bg-neutral-0 md:bg-brand-900 flex flex-col pb-6 md:py-8 ${
          zIndexes.SIDEBAR
        }`
      )}
      style={{ transitionDelay: `${HOVER_DELAY}ms` }}>
      {!full && (
        <Link
          shallow
          href='/dashboard'
          className={
            'relative flex items-center justify-start pl-3 mb-6 w-full'
          }>
          <span
            className={twMerge(
              'transition-opacity duration-150',
              isHovering && 'opacity-0'
            )}>
            <DolfinIcon color={'text-neutral-0'} size={32} />
          </span>
          <span
            className={twMerge(
              'pointer-events-none absolute left-5 top-1/2 -translate-y-1/2 opacity-0 transition-opacity duration-150',
              isSidebarFullyExpanded && 'opacity-100'
            )}>
            <DolfinLogo className='text-[#DAFF87]' width={100} />
          </span>
        </Link>
      )}

      <div
        className={`flex-grow flex flex-col ${
          full
            ? 'justify-start overflow-y-scroll pt-6 scrollbar-hide'
            : 'justify-between md:py-12 '
        }`}>
        <ul className={'w-full flex flex-col gap-2 px-0'}>
          {TOP_SECTION.map(item => (
            <SidebarItem
              item={item}
              closeSidebar={closeSidebar}
              isActive={isActive}
              isChildrenActive={isChildrenActive}
              visibleSubSidebar={visibleSubSidebar}
              setVisibleSubSidebar={setVisibleSubSidebar}
              full={full}
              key={item.id}
            />
          ))}
          <p
            className={
              'uppercase text-neutral-500 text-xs pl-6 md:pl-5 pt-8 pb-2 md:pb-0 pr-2 md:opacity-0 md:group-hover:opacity-100 overflow-hidden border-0'
            }>
            <Trans>Configuration</Trans>
          </p>
          <div
            className={
              'w-[2.45rem] h-[1px] bg-neutral-500 mx-2 hidden md:flex md:group-hover:w-0 md:group-hover:opacity-30 transition-[width,opacity] duration-300'
            }
            style={{ transitionDelay: `${HOVER_DELAY}ms` }}></div>
          {MIDDLE_SECTION.map(item => (
            <SidebarItem
              item={item}
              closeSidebar={closeSidebar}
              isActive={isActive}
              isChildrenActive={isChildrenActive}
              visibleSubSidebar={visibleSubSidebar}
              setVisibleSubSidebar={setVisibleSubSidebar}
              full={full}
              key={item.id}
            />
          ))}
        </ul>

        <ul className={'w-full flex flex-col gap-2 px-0 pt-2'}>
          <SidebarItem
            item={{
              id: TABS.SETTINGS,
              label: t`Settings`,
              href: '/dashboard?page=settings',
              Icon: Settings02
            }}
            isActive={isActive}
            isChildrenActive={isChildrenActive}
            visibleSubSidebar={visibleSubSidebar}
            setVisibleSubSidebar={setVisibleSubSidebar}
            closeSidebar={closeSidebar}
            full={full}
          />
        </ul>
      </div>

      {isFlipperEnabledForViewer({
        organizationId: selectedOrganization?.id,
        email: currentUser?.email
      }) && <FlipperChatButton />}

      <Link
        shallow
        className={'w-full'}
        href={
          isAdmin || isSupervisor
            ? `/dashboard?page=user&id=${currentUser?.id}`
            : '/dashboard'
        }>
        <span
          className={
            'text-neutral-700 md:text-neutral-100 pl-[0.45rem] group-hover:pl-2 transition-all duration-300 flex items-center h-10'
          }
          style={{ transitionDelay: `${HOVER_DELAY}ms` }}>
          <Avatar
            withDetails={isHovering}
            size={isHovering ? Sizes.SMALL : Sizes.SEMI_LARGE}
          />
        </span>
      </Link>
    </div>
  )
}

const Sidebar = () => {
  const [showMobileSidebar, setShowMobileSidebar] = useState(false)

  return (
    <>
      <div className={'hidden md:flex'}>
        <SidebarElement closeSidebar={() => setShowMobileSidebar(false)} />
      </div>

      {showMobileSidebar && (
        <div
          className={`fixed left-0 top-10 md:top-14 w-screen ${zIndexes.SIDEBAR}`}>
          <SidebarElement
            full
            closeSidebar={() => setShowMobileSidebar(false)}
          />
        </div>
      )}

      <div className='md:hidden z-20 fixed left-0 top-0 border-b bg-neutral-0 w-screen flex items-center justify-between py-3 px-6'>
        <Link shallow href='/dashboard'>
          <DolfinIcon size={28} />
        </Link>

        <div className={'flex gap-3'}>
          <Notifications />

          <Button
            className={'outline-none text-neutral-900'}
            onPress={() => setShowMobileSidebar(s => !s)}>
            {showMobileSidebar ? (
              <XMarkIcon width={18} />
            ) : (
              <Bars3Icon width={18} />
            )}
          </Button>
        </div>
      </div>
    </>
  )
}

export default Sidebar
